import React, {
  ComponentProps,
  useState,
  useRef,
  useMemo,
  useEffect,
  MouseEventHandler,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTeamProfileUrl } from 'utils';
import { useRequestStatus } from 'RequestStatusModule/hooks/useRequestStatus';
import { TagContainer } from 'SandboxModule/styles';
import { teamStandardTagsList } from '@constants';
import { createTeam } from 'TeamModule/thunks';
import { LinearBuffer } from 'components/Progress';
import { useArrayTagsInput } from 'hooks/useArrayItems/useArrayTagsInput';
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import SimpleModal from 'components/SimpleModal';
import { CreateTeamSuccessResponse } from 'TeamModule/types';
import { BaseDivider } from 'components/BaseDivider';
import TeamTagsAutocomplete from 'components/TeamTagsAutocomplete';
import { CsmSelector } from 'CsmModule/components/CsmSelector';
import { useCsmSelector } from 'CsmModule/hooks/useCsmSelector';
import { useCsmAssignments } from 'CsmModule/hooks/useCsmAssignments';
import { useNotification } from 'hooks/useNotification';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import {
  ALL_SIZE_TAGS,
  LARGE_TEAM_TAG,
  TEAM_SIZE_BUCKETS,
  TEAM_SIZE_HELPER_TEXT,
  TEAM_SIZE_LABEL,
  TEAM_SIZE_OPTIONS,
  TeamSizeBucket,
  sizeTagForSelection,
} from 'TeamModule/constants/teamSize';
import { CreateTeamDedicatedRealmWarning } from './CreateTeamDedicatedRealmWarning';
import { useCreateRealm } from 'SandboxModule/hooks/useCreateRealm';
import { useRealmIdGeneration } from 'SandboxModule/hooks/useRealmIdGeneration';
import { CreateRealmParams } from 'SandboxModule/types';
import { RealmTypes } from 'RealmModule/constants/realm';
import { selectAuthUser } from 'selectors';

const requestStatusId = 'create-team-modal';

// Same values CreateSandboxForm and the subscription form use. Duplicated rather
// than shared because they are file-local consts there too; worth hoisting if a
// fourth caller appears.
const defaultRegion = 'us-east-1';
const defaultDomain = 'mosaicapp.com';

type InitialValuesType = {
  email: string;
  company: string;
  csm_support_account: boolean;
};

const initialValues = {
  email: '',
  company: '',
  csm_support_account: true,
};

export const CreateTeamModal = ({ toggle }: { toggle: () => void }) => {
  const [createTeamFormFields, setFields] = useState<InitialValuesType>(initialValues);
  const dispatch = useDispatch();
  const { requestStatus } = useRequestStatus({
    requestStatusId,
  });
  const { isRequesting: isCreatingTeam } = requestStatus || {};
  const { handleCheckboxChange, standardTags, tagsList, applyTagsChange, tagError } =
    useArrayTagsInput();

  // Tenant/realm selection using the reusable dropdown flow hook
  const { onSelectTenantRealm, onSelectTenantOnly, selectedTenant, onSelectRealm, selectedRealm } =
    useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);

  const { showError, showSuccess, showWarning } = useNotification();
  const authUser = useSelector(selectAuthUser);

  /**
   * GLX-1922. Kept out of `createTeamFormFields` on purpose: that object is driven
   * by `onCreateTeamFormChange`, which is typed for
   * React.ChangeEvent<HTMLInputElement> and cannot accept a MUI SelectChangeEvent.
   */
  const [teamSize, setTeamSize] = useState<TeamSizeBucket | ''>('');
  const [growthOverride, setGrowthOverride] = useState(false);
  /** The operator escape hatch - see the existing-realm override below. */
  const [useExistingRealm, setUseExistingRealm] = useState(false);
  const [showDedicatedRealmWarning, setShowDedicatedRealmWarning] = useState(false);

  const isLargeTeam = teamSize === TEAM_SIZE_BUCKETS.large;
  // At 100+ a dedicated realm is what the selection means, so the override is
  // forced on and locked. Opting out is still possible, but only through the
  // separate existing-realm override.
  const effectiveGrowthOverride = isLargeTeam || growthOverride;
  // Derived, never read raw: `useExistingRealm` can hold a stale true while its
  // checkbox is unmounted, and a stale bypass must not reach the wire.
  const bypassEngaged = effectiveGrowthOverride && useExistingRealm;
  const wantsDedicatedRealm = effectiveGrowthOverride && !bypassEngaged;

  /**
   * The tags actually submitted: whatever the operator picked, plus exactly one
   * size tag derived from the dropdown.
   *
   * Derived at render rather than pushed into `tagsList` by an effect. An effect
   * cannot see later edits to the tag list without depending on it, and depending
   * on it loops - so deleting the derived chip used to strip the size marker for
   * good. Worse, on the bypass path that produced a `bypassLargeTeamGuard` on a
   * body the server no longer reads as large, recording a bypass that did nothing.
   *
   * Consequence worth knowing: the size tag is owned by the dropdown, so deleting
   * its chip re-appears it. That is the honest behaviour - the way to remove it is
   * to change the size.
   */
  const submittedTags = useMemo(() => {
    const derived = sizeTagForSelection({ teamSize, growthOverride: effectiveGrowthOverride });
    const withoutSizeTags = tagsList.filter((tag) => !ALL_SIZE_TAGS.includes(tag));
    return derived ? [...withoutSizeTags, derived] : withoutSizeTags;
  }, [tagsList, teamSize, effectiveGrowthOverride]);

  /**
   * Picking the `100+` tag by hand is the same intent as ticking the override, and
   * the server-side guard keys on the tag either way - so reflect it in the
   * checkbox rather than letting the two disagree.
   *
   * Deliberately an event handler and not an effect on `tagsList`: as an effect it
   * races the reconciliation above, and un-ticking the override would re-tick
   * itself from the tag it had just been asked to remove.
   */
  const onTagsChange = (next: string[], meta?: { preValidated?: Set<string> }) => {
    const addedLargeTeamTag =
      next.includes(LARGE_TEAM_TAG) && !submittedTags.includes(LARGE_TEAM_TAG);
    // The size tags are this form's own vocabulary and can never clear the
    // freeform `[a-z0-9 ]` rule, so hand-picking `100+` would otherwise apply the
    // tag and report "cannot contain special characters" about it in the same
    // breath. Only the tags actually in `next` are excused, not the whole list.
    const preValidated = new Set(meta?.preValidated);
    ALL_SIZE_TAGS.filter((tag) => next.includes(tag)).forEach((tag) => preValidated.add(tag));
    applyTagsChange(next, { preValidated });
    if (addedLargeTeamTag) {
      setGrowthOverride(true);
    }
  };

  const onTeamSizeChange = (event: SelectChangeEvent) => {
    setTeamSize(event.target.value as TeamSizeBucket);
    // Changing the size is a fresh decision about realm strategy, so the escape
    // hatch does not carry over - otherwise its checkbox can reappear
    // pre-ticked and silently skip the warning.
    setUseExistingRealm(false);
  };

  const onGrowthOverrideChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGrowthOverride(event.target.checked);
    if (!event.target.checked) {
      setUseExistingRealm(false);
    }
  };

  const { onCsmSelectorClick, selectedCsm } = useCsmSelector({
    accountId: null,
  });

  const handleCsmSelectorClick: ComponentProps<typeof CsmSelector>['onClick'] = (e) => {
    onCsmSelectorClick({
      target: e.currentTarget,
    });
  };

  const { createCsmAssignment } = useCsmAssignments({});

  // generate company slug for email
  // Valid domain names can only contain letters and numbers
  const generateCompanySlug = (companyName: string) => {
    return companyName.toLowerCase().replace(/[^a-z0-9]/g, ''); // Remove all non-alphanumeric characters
  };

  // generate CSM support email
  const generateCsmSupportEmail = (companyName: string) => {
    const companySlug = generateCompanySlug(companyName);
    return `import@${companySlug}.mosaicapp.com`;
  };

  const onCreateTeamFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFields((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    // TenantSelector takes no disabled prop, so the lock lives here. Changing the
    // tenant mid-flight would send the dedicated-realm request to a different one
    // than the operator confirmed.
    if (!e.currentTarget || isSubmitting) {
      return;
    }
    // The combined flow chains straight into the realm dropdown and only commits
    // the tenant once a realm is picked. With a dedicated realm about to be
    // created there is no realm to pick, so that chain would either force an
    // irrelevant choice or dead-end on a tenant with no existing realms.
    if (wantsDedicatedRealm) {
      onSelectTenantOnly({ target: e.currentTarget });
      return;
    }
    if (realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
      });
    }
  };

  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({
        target: e.currentTarget,
        tenantId: selectedTenant.id,
      });
    }
  };

  const validateCreateTeamForm = (): boolean => {
    if (!selectedTenant) {
      showError('Please select a tenant before creating a team');
      return false;
    }

    const { company, email, csm_support_account } = createTeamFormFields;
    // Validate company name is not empty or whitespace
    if (!company || company.trim().length === 0) {
      showError('Please enter a valid company name.');
      return false;
    }

    const shouldUseCsmAccount = csm_support_account;

    // Validate email when not creating CSM support account
    if (!shouldUseCsmAccount && (!email || email.trim().length === 0)) {
      showError('Please enter a valid email address.');
      return false;
    }

    // Validate that the company slug is not empty (handles Unicode-only or special-char-only names)
    const companySlug = generateCompanySlug(company);
    if (shouldUseCsmAccount && companySlug.length === 0) {
      showError(
        'Company name must contain at least one letter or number for CSM support account email.',
      );
      return false;
    }

    // The bypass means "put this large team in the realm I chose" - without a realm
    // it would wave the guard through and let rails fall back to the tenant's
    // primary realm, which is the exact outcome the guard exists to prevent.
    if (bypassEngaged && !selectedRealm) {
      showError(
        'Select the realm this team should be created in, or uncheck the existing-realm option.',
      );
      return false;
    }

    // GLX-1922: required on every team creation. Also guarded by
    // disableConfirmButton; this is belt-and-braces, matching the
    // file's existing validate-then-toast style.
    if (!teamSize) {
      showError(`Please select the ${TEAM_SIZE_LABEL.toLowerCase()}.`);
      return false;
    }

    return true;
  };

  /** Today's path: sign the team up directly into the selected realm. */
  const createTeamInSelectedRealm = () => {
    if (!selectedTenant) {
      return;
    }
    const { company, email, csm_support_account } = createTeamFormFields;
    const shouldUseCsmAccount = csm_support_account;
    const csmAccountEmail = generateCsmSupportEmail(company);

    const params = {
      email: shouldUseCsmAccount ? csmAccountEmail : email,
      company,
      is_csm: shouldUseCsmAccount,
      tenant_name: selectedTenant.tenantName,
      realm_id: selectedRealm?.realm_id || undefined, // Include realm if selected
      // GLX-1925: galaxy-server rejects a `100+` team aimed at a shared or
      // already-occupied realm. Reaching this line with the large tag means the
      // user deliberately chose an existing realm, so say so explicitly rather
      // than letting them hit a 422 they cannot act on. Recorded in activity_log.
      ...(bypassEngaged ? { bypassLargeTeamGuard: true } : {}),
      meta: {
        onSuccess: ({ response }: { response: { data: CreateTeamSuccessResponse } }) => {
          const { team_id, team_created, tag_warning } = response.data;

          if (!team_id || !team_created) {
            showError('Create team failed, please try again');
            return;
          }

          // The team exists but its tags did not persist. Silence here would tell
          // the operator "created successfully" while the 100+ tag that drives
          // large-team behaviour is missing, with no prompt to re-apply it.
          if (tag_warning) {
            showWarning(tag_warning);
          }

          // If `selectedCsm`, call `createCsmAssignment`
          if (selectedCsm) {
            createCsmAssignment({
              mosaicAccountId: selectedCsm.accountId,
              mosaicTeamId: team_id,
              csmName: selectedCsm.name,
              startDate: new Date().toISOString(),
            });
          }
          setTimeout(() => {
            window.location.href = getTeamProfileUrl({
              teamId: +team_id,
              tenant: selectedTenant.tenantName,
              realmId: selectedRealm?.realm_id || undefined,
            });
          }, 2000);
        },
        onFailure: ({ error }: any) => {
          const apiMessage = error?.data?.message || error?.message;
          const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : ', please try again';
          showError(`Create team failed${apiMessageSuffix}`);
        },
        requestStatusId,
      },
      galaxy_props: {
        tags: submittedTags,
      },
    };
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(createTeam(params) as any);
  };

  /**
   * GLX-1924: the one-call flow. The FE hands the team over with the realm
   * request and is done - the realm-creation job signs the team up as its final
   * step, once the realm is proven serveable. Nothing is polled, and the flow
   * survives this tab closing.
   *
   * `useCreateRealm` already emits AppEvents.CreateRealmSuccess and refreshes the
   * realm tables, so this only has to supply the payload and close the modal.
   */
  const buildTeamSignupEnvelope = (): NonNullable<CreateRealmParams['teamSignup']> => {
    const { company, email, csm_support_account } = createTeamFormFields;
    const shouldUseCsmAccount = csm_support_account;
    return {
      // Only what rails' signup permit accepts. tenant_name/realm_id are
      // deliberately absent: the job supplies both as headers, and rails' strong
      // params drop them from the body anyway.
      signup: {
        email: shouldUseCsmAccount ? generateCsmSupportEmail(company) : email,
        company,
        is_csm: shouldUseCsmAccount,
        galaxy_props: { tags: submittedTags },
      },
      ...(selectedCsm ? { csmName: selectedCsm.name, csmAccountId: selectedCsm.accountId } : {}),
    };
  };

  const { createRealm, isCreatingRealm } = useCreateRealm({
    onSuccess: () => {
      // Deliberately not fired optimistically before the request: useCreateRealm
      // shows an error toast on failure, so an up-front "creating..." message
      // would sit next to it and contradict it. The team does not exist yet
      // either, so there is nothing to navigate to - close and let the Realms and
      // Teams tables show the result when the job finishes.
      showSuccess(
        `Creating a dedicated realm for ${createTeamFormFields.company}. It takes about 5-10 minutes, and the team will appear in the Realms and Teams tables when it is ready.`,
      );
      toggle();
    },
  });

  const onRealmIdGenerated: Parameters<typeof useRealmIdGeneration>[0]['onSuccess'] = ({
    response,
  }) => {
    const realmId = response.data;
    const { company } = createTeamFormFields;
    if (!realmId || !authUser || !selectedTenant) {
      // realmId is the only one that can still be missing here; the other two are
      // checked before the id is reserved.
      showError('Create team failed, please try again');
      return;
    }

    const params: CreateRealmParams = {
      realmId,
      teamName: company,
      domain: defaultDomain,
      region: defaultRegion,
      // Subscription is required: galaxy-server rejects teamSignup on anything else.
      realmType: RealmTypes.subscription,
      tenantName: selectedTenant.tenantName,
      tags: submittedTags,
      metaPayload: { name: authUser.account.email },
      teamSignup: buildTeamSignupEnvelope(),
    };

    createRealm(params);
  };

  const { generateRealmId, isGeneratingRealmId } = useRealmIdGeneration({
    onSuccess: onRealmIdGenerated,
  });

  /**
   * The dedicated path runs on its own mutations, not the legacy createTeam thunk,
   * so `isCreatingTeam` says nothing about it. Without this a second click queues
   * a second realm id and a second realm - leaving an orphan behind.
   */
  const isCreatingDedicatedRealm = isGeneratingRealmId || isCreatingRealm;

  /**
   * Every control locks while either path is in flight. The dedicated path reads
   * the form at callback time - `onRealmIdGenerated` and `buildTeamSignupEnvelope`
   * run after `generateRealmId` resolves - so leaving the inputs editable let an
   * operator generate a realm id for one team name and then create the realm with
   * another, or clear a required field entirely.
   */
  const isSubmitting = isCreatingTeam || isCreatingDedicatedRealm;

  /**
   * Released when the mutations settle rather than on a timer: success unmounts
   * this modal, and a failure flips isSubmitting back to false, so a retry stays
   * possible without the latch ever getting stuck.
   */
  const dedicatedRealmSubmitted = useRef(false);
  useEffect(() => {
    if (!isSubmitting) {
      dedicatedRealmSubmitted.current = false;
    }
  }, [isSubmitting]);

  const createTeamWithDedicatedRealm = () => {
    // Checked before generateRealmId, not inside its callback: reserving an id we
    // then refuse to use leaves it stranded for nothing.
    if (!authUser || !selectedTenant) {
      showError('Create team failed, please try again');
      return;
    }
    // Same-tick re-entry guard. disableConfirmButton only takes effect once
    // react-query has committed isPending, so a held Enter key or a synthetic
    // click burst could fire twice before the button disables - and two realms
    // for one team means an orphan with its own databases, topics and tables.
    if (dedicatedRealmSubmitted.current) {
      return;
    }
    dedicatedRealmSubmitted.current = true;
    generateRealmId({ teamName: createTeamFormFields.company });
  };

  const submitCreateTeamForm = () => {
    if (!validateCreateTeamForm()) {
      return;
    }
    if (wantsDedicatedRealm) {
      // Nothing is created until the warning is acknowledged.
      setShowDedicatedRealmWarning(true);
      return;
    }
    createTeamInSelectedRealm();
  };

  const acknowledgeDedicatedRealmWarning = () => {
    setShowDedicatedRealmWarning(false);
    createTeamWithDedicatedRealm();
  };

  const CreateTeamModalBody = (
    <div>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Organization
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
          <TenantSelector onClick={onTenantSelectorClick} tenantId={selectedTenant?.id || null} />
          <RealmSelector
            onClick={onRealmSelectorClick}
            // Blank while a dedicated realm is coming: the selector holds the
            // app-bar realm by default, and showing it next to "a new dedicated
            // realm will be created" implies that realm is the target.
            realmId={wantsDedicatedRealm ? '' : selectedRealm?.realm_id || ''}
            ref={realmDropdownRef}
            disabled={wantsDedicatedRealm || isSubmitting}
            tooltip={
              wantsDedicatedRealm
                ? 'A new dedicated realm will be created for this team'
                : undefined
            }
          />
        </Box>
      </Box>
      <p>Enter information to sign up for a new team</p>
      <form>
        <Box display={'flex'} gap={5} flexDirection={'column'}>
          <FormControlLabel
            control={
              <Checkbox
                checked={createTeamFormFields.csm_support_account}
                onChange={onCreateTeamFormChange}
                name={'csm_support_account'}
                disabled={isSubmitting}
              />
            }
            label={'Create the team with a CSM support account'}
          />

          {createTeamFormFields.csm_support_account ? (
            <Box component={'div'} fontSize={14}>
              A support account email will be created automatically:{' '}
              <strong>
                {createTeamFormFields.company
                  ? generateCsmSupportEmail(createTeamFormFields.company)
                  : 'import@companyname.mosaicapp.com'}
              </strong>
            </Box>
          ) : (
            <TextField
              required={!createTeamFormFields.csm_support_account}
              onChange={onCreateTeamFormChange}
              value={createTeamFormFields.email || ''}
              label="Email Address"
              type="email"
              name="email"
              placeholder="Email"
              fullWidth
              variant="outlined"
              disabled={isSubmitting}
            />
          )}

          <TextField
            required
            onChange={onCreateTeamFormChange}
            value={createTeamFormFields.company || ''}
            label="Company name"
            name="company"
            placeholder="My Company"
            type="text"
            fullWidth
            variant="outlined"
            disabled={isSubmitting}
          />
          {/* ------------------------- GLX-1922: expected team size ------------------------ */}
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <FormControl fullWidth required disabled={isSubmitting}>
              <InputLabel id="expected-team-size-label">{TEAM_SIZE_LABEL}</InputLabel>
              <Select
                labelId="expected-team-size-label"
                label={TEAM_SIZE_LABEL}
                name="expected_team_size"
                value={teamSize}
                onChange={onTeamSizeChange}
                inputProps={{ 'aria-describedby': 'expected-team-size-helper' }}
              >
                {TEAM_SIZE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText id="expected-team-size-helper">
                {TEAM_SIZE_HELPER_TEXT}
              </FormHelperText>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={effectiveGrowthOverride}
                  onChange={onGrowthOverrideChange}
                  name={'growth_override'}
                  // At 100+ this is what the selection means, so it is locked on.
                  disabled={isSubmitting || isLargeTeam}
                />
              }
              label={'Create a dedicated realm anyway (known upcoming growth)'}
            />

            {/*
              The escape hatch (spec open question 1). Only offered once a dedicated
              realm would otherwise be created, so it stays out of the way on the
              common path, and never pre-selected.
            */}
            {effectiveGrowthOverride && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={useExistingRealm}
                    onChange={(event) => setUseExistingRealm(event.target.checked)}
                    name={'use_existing_realm'}
                    disabled={isSubmitting}
                  />
                }
                label={
                  <Typography variant="caption">
                    Use an existing realm instead - no dedicated realm will be created
                  </Typography>
                }
              />
            )}
          </Box>

          <Box display={'flex'}>
            <Box marginRight={3}>Select the Customer Success Manager: </Box>
            <CsmSelector
              accountId={selectedCsm?.accountId || null}
              onClick={handleCsmSelectorClick}
              disabled={isSubmitting}
            />
          </Box>
        </Box>

        <BaseDivider />
        {/* ----------------------------------- add tags ---------------------------------- */}

        <FormControl component="fieldset" fullWidth>
          <Box component="h3">Add tags to this team</Box>
          <TagContainer>
            <div className="initial-tag-div">
              <FormGroup>
                {teamStandardTagsList.map((item) => (
                  <FormControlLabel
                    key={`${item.value}-${standardTags[item.value as keyof typeof standardTags]}`}
                    control={
                      <Checkbox
                        checked={standardTags[item.value as keyof typeof standardTags]}
                        onChange={handleCheckboxChange}
                        name={item.name}
                        value={item.value}
                        disabled={isSubmitting}
                      />
                    }
                    label={item.label}
                  />
                ))}
              </FormGroup>
            </div>
            <div className="add-tag-div">
              <TeamTagsAutocomplete
                value={submittedTags}
                onChange={onTagsChange}
                helperText={tagError}
                error={!!tagError}
                disabled={isSubmitting}
                placeholder="Select or type a tag"
              />
            </div>
          </TagContainer>
        </FormControl>
        <Box> {isSubmitting ? <LinearBuffer /> : ''}</Box>
      </form>
    </div>
  );

  // Body-swap confirmation, following CreateSandboxModal. `closeOnConfirm` is
  // deliberately NOT passed: SimpleModal would close the modal on confirm and the
  // acknowledge step could never hand back to the form.
  return (
    <SimpleModal
      // Nothing has been created at the warning step, so there is nothing to
      // cancel - returning to the form preserves everything already typed.
      onClose={() => {
        // Unmounting mid-flight drops useCreateRealm's onSuccess (toast, event,
        // table refresh) and lets the operator reopen and submit a second realm.
        // disableCancelButton only covers the footer button, not backdrop/Escape.
        if (isSubmitting) {
          return;
        }
        if (showDedicatedRealmWarning) {
          setShowDedicatedRealmWarning(false);
          return;
        }
        toggle();
      }}
      cancelButtonText={showDedicatedRealmWarning ? 'Back' : undefined}
      open
      onConfirm={
        showDedicatedRealmWarning ? acknowledgeDedicatedRealmWarning : submitCreateTeamForm
      }
      confirmButtonText={
        showDedicatedRealmWarning
          ? 'Create dedicated realm'
          : isSubmitting
            ? 'Creating...'
            : 'Submit'
      }
      headerText={'Create New Team'}
      disableConfirmButton={
        isSubmitting ||
        (!showDedicatedRealmWarning && (!teamSize || (bypassEngaged && !selectedRealm)))
      }
      disableCancelButton={isSubmitting}
      Body={showDedicatedRealmWarning ? <CreateTeamDedicatedRealmWarning /> : CreateTeamModalBody}
    />
  );
};
